import './kitStyles';
import { useLayoutEffect, type RefObject } from 'react';

/**
 * WEB-3310 юнит 2а-1 — место всплывающих кита (эталон `flPlace` / `flFix`,
 * `design/cabinet-rescin/kit.jsx`). Всплывающее стоит `position: fixed` по
 * координатам хозяина — так его не режет `overflow` таблицы, карточки и окна.
 * Задуманное место берётся из обычной раскладки (CSS раздела), дальше: не
 * помещается снизу — вверх (и наоборот), по бокам — прижатие к краю экрана с
 * отступом 8, не помещается ни так ни так — где места больше, с прокруткой
 * внутри (высота не меньше 80); не влезает вправо — правым краем к правому краю
 * якоря; `z-index` не меньше 1000.
 *
 * Отличие от эталона (решение оркестратора 23.10): не наблюдатель всего
 * документа (`flInit`), а хук `useFloatPlace(ref, open)` — каждое всплывающее
 * ставит себя само, пока открыто; антд-страниц кабинета это не касается.
 * Правила — чистыми функциями (`floatPlace`, `tipPlace`, `tipLayerPlace`), их
 * сверяет тест на числах.
 */
export const FL = 8;

/**
 * Шторки телефона из `FL_SHEET` эталона — только части кита: меню, списки
 * выбора и окно выбора дат (классы экранов `.hx-pop`, `.an-pop`, `.qm-pop` — не кит).
 */
export const FL_SHEET = '.pop.menu, .pop[role=listbox], .kdr-pop';

export interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface FloatInput {
  /** Задуманное место всплывающего (обычная раскладка). */
  natural: Box;
  /** Якорь — родитель всплывающего. */
  anchor: Box;
  /** Высота всплывающего при итоговой ширине. */
  height: number;
  vw: number;
  vh: number;
}

export interface FloatPlace {
  x: number;
  y: number;
  width: number;
  /** Высота с прокруткой внутри, если всплывающее не помещается; иначе null. */
  maxHeight: number | null;
}

/** Правила места эталона (`flPlace`) на числах. */
export function floatPlace({ natural: n, anchor: a, height: h, vw: W, vh: H }: FloatInput): FloatPlace {
  const w = Math.min(n.width, W - 2 * FL);
  const down = n.top >= a.bottom - 2 || (n.top > a.top && n.bottom > a.bottom);
  const gap = down ? Math.max(0, n.top - a.bottom) : Math.max(0, a.top - n.bottom);
  const below = H - FL - (a.bottom + gap);
  const above = a.top - gap - FL;
  const goDown = down ? h <= below || below >= above : !(h <= above || above >= below);
  const room = goDown ? below : above;
  const maxHeight = h > room ? Math.max(80, room) : null;
  const hh = Math.min(h, Math.max(80, room));
  const y = Math.max(FL, Math.min(H - FL - hh, goDown ? a.bottom + gap : a.top - gap - hh));
  const x0 = n.left + w > W - FL ? a.right - w : n.left;
  return { x: Math.max(FL, Math.min(W - FL - w, x0)), y, width: w, maxHeight };
}

/** `z-index` всплывающего: ниже 1000 (или `auto`) — поднять до 1000, иначе не трогать. */
export function floatZ(z: string): string | null {
  return z === 'auto' || +z < 1000 ? '1000' : null;
}

/** Место подсказки `Tip`: снизу хозяина по центру, снизу нет места — сверху. */
export function tipPlace(r: Box, w: number, h: number, W: number, H: number): { x: number; y: number } {
  const x = Math.max(FL, Math.min(W - w - FL, r.left + r.width / 2 - w / 2));
  const below = r.bottom + 8 + h <= H - FL || r.top - 8 - h < FL;
  return { x, y: below ? r.bottom + 8 : r.top - 8 - h };
}

/** Место слоя подсказки `data-tip`: над хозяином, сверху нет места — под ним. */
export function tipLayerPlace(r: Box, w: number, h: number, W: number, H: number): { x: number; y: number } {
  const up = r.top - 8 - h >= FL || r.bottom + 8 + h > H - FL;
  return {
    x: Math.max(FL, Math.min(W - FL - w, r.left + r.width / 2 - w / 2)),
    y: up ? Math.max(FL, r.top - 8 - h) : r.bottom + 8,
  };
}

/**
 * `fixed` внутри предка с `transform` / `filter` / `backdrop-filter` отсчитывается
 * от него — поправка по факту (эталон `flFix`).
 */
export function flFix(el: HTMLElement, x: number, y: number): void {
  el.style.left = x + 'px';
  el.style.top = y + 'px';
  const r = el.getBoundingClientRect();
  if (Math.abs(r.left - x) > 0.5) el.style.left = 2 * x - r.left + 'px';
  if (Math.abs(r.top - y) > 0.5) el.style.top = 2 * y - r.top + 'px';
}

/**
 * Ставит всплывающее на место (эталон `flPlace`); якорь — родитель узла. На
 * телефоне шторки (`FL_SHEET`) только возвращают задуманный стиль — место у них
 * по CSS.
 */
export function placeFloat(pop: HTMLElement): void {
  if (!pop.isConnected || getComputedStyle(pop).display === 'none') return;
  const anchor = pop.parentElement;
  if (!anchor) return;
  if (pop.dataset.flOrig === undefined) pop.dataset.flOrig = pop.getAttribute('style') || '';
  pop.setAttribute('style', pop.dataset.flOrig);
  // телефон: меню, списки выбора и окно дат — шторкой снизу по CSS; свои координаты в узле перебивали её (эталон 24.10)
  if (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 640px)')?.matches && pop.matches(FL_SHEET)) {
    return;
  }
  const natural = pop.getBoundingClientRect();
  const a = anchor.getBoundingClientRect();
  const W = window.innerWidth;
  const H = window.innerHeight;
  const w = Math.min(natural.width, W - 2 * FL);
  const z = floatZ(getComputedStyle(pop).zIndex);
  Object.assign(pop.style, {
    position: 'fixed',
    right: 'auto',
    bottom: 'auto',
    transform: 'none',
    margin: '0',
    width: w + 'px',
    maxWidth: 'none',
    minWidth: '0',
  });
  if (z) pop.style.zIndex = z;
  const p = floatPlace({ natural, anchor: a, height: pop.getBoundingClientRect().height, vw: W, vh: H });
  if (p.maxHeight !== null) {
    pop.style.maxHeight = p.maxHeight + 'px';
    pop.style.overflowY = 'auto';
  }
  flFix(pop, p.x, p.y);
}

/**
 * Хук места: пока `open`, ставит узел `ref` на место — на открытии, прокрутке
 * любого слоя (фаза захвата), смене размера экрана и узла, и по кадру, если
 * якорь сдвинулся без прокрутки (страница отъехала, выше выросло содержимое).
 */
export function useFloatPlace(ref: RefObject<HTMLElement | null>, open: boolean): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!open || !el) return undefined;
    const place = () => placeFloat(el);
    const at = () => {
      const a = el.parentElement?.getBoundingClientRect();
      return a ? a.left + ',' + a.top + ',' + a.width : '';
    };
    place();
    let last = at();
    let raf = 0;
    const tick = () => {
      const k = at();
      if (k !== last) {
        last = k;
        place();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
    ro?.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      ro?.disconnect();
    };
  }, [ref, open]);
}
